import React, { useEffect, useState, useRef } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Input,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  DatePicker,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { createStyles } from "antd-style";
import {
  HomeOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { theme } from "antd";
import SviconLedgerModal from "./SviconLedgerModal";
import { showSuccess, showError } from "../../../../components/errorHandler";
import type { PopconfirmProps, TableColumnsType } from "antd";
import { useCurrentModule } from "../../../../hooks/useCurrentModule";
import moment, { Moment } from "moment";
import ImportLedgerModal from "./ImportLedgerModal";

const { RangePicker } = DatePicker;

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));
interface LinkItem {
  link: string;
  description?: string;
}
interface BankItem {
  bank_name: string;
  ifsc: string;
  acc_no: string;
  address?: string;
}
interface AddressItem {
  type?: "residence" | "office";
  address?: string;
  landmark?: string;
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
}
// Updated Ledger interface with all required fields
interface Ledger {
  ledger_id: number;
  ledger_first_name: string;
  ledger_last_name?: string;
  ledger_account_head?: number;
  ledger_sub_account_head?: number;
  ledger_mobile_no?: string;
  ledger_company_name?: string;
  ledger_occupation?: number;
  ledger_designation?: number;
  ledger_opening_balance?: number;
  ledger_opening_type?: string;
  ledger_email?: string;
  ledger_gstin?: string;
  ledger_pan_no?: string;
  ledger_cin_no?: string;
  ledger_msme_reg_no?: string;
  ledger_import_export_code?: string;
  ledger_bill_to_bill?: "Yes" | "No";
  ledger_reserve_fund?: string;
  ledger_tds?: string;
  ledger_deals_in_company?: string[];
  ledger_deals_in_product?: string[];
  ledger_interest_rate?: number;
  ledger_interest_style?: string;
  ledger_credit_limit_day?: number;
  ledger_credit_limit_amount?: number;
  ledger_dob?: Dayjs | null | undefined;
  ledger_marriage_anniversary?: Dayjs | null | undefined;
  ledger_work_anniversary?: Dayjs | null | undefined;
  ledger_offer_festival?: number;
  ledger_status: number;
  ledger_created_on: string;
  ah_name: string;
  parent_head_name: string;
  links?: LinkItem[];
  banks?: BankItem[];
  addresses?: AddressItem[];
}

const SviconLegders: React.FC = () => {
  const hasFetchedAccountHeads = useRef(false);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [form] = Form.useForm();
  const [mobileSearch, setMobileSearch] = useState("");
  const [accountHeadOptions, setAccountHeadOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [selectedAccountHead, setSelectedAccountHead] = useState<number | null>(
    null,
  );

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Ledger | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
    showError("Click on No");
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const handleMobileSearch = (value: string) => {
    setMobileSearch(value);
    fetchLedger(
      pagination.current,
      pagination.pageSize,
      value,
      selectedAccountHead,
    ); // fetch filtered ledgers on the server
  };

  const fetchLedger = async (
    page = 1,
    limit = 10,
    mobile?: string,
    accountHead?: number | null,
    startDate?: string, // YYYY-MM-DD
    endDate?: string, // YYYY-MM-DD
  ) => {
    try {
      let url = `h-ledgers?page=${page}&limit=${limit}`;
      if (mobile) url += `&mobile=${encodeURIComponent(mobile)}`;
      if (accountHead) url += `&account_head=${accountHead}`;

      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;

      const response = await apiRequest<{
        data: Ledger[];
        page: number;
        limit: number;
        total: number;
      }>({
        method: "get",
        url,
      });
      setLedgers(response.data);
      setPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch (error) {
      showError("Failed to fetch Ledger");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLedger(
      pagination.current,
      pagination.pageSize,
      mobileSearch,
      selectedAccountHead,
    );
  }, []);

  const handleEditClick = (record: Ledger) => {
    setIsEditMode(true);
    setInitialValues(record); // Set full ledger record here
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchLedger(
      pagination.current,
      pagination.pageSize,
      mobileSearch,
      selectedAccountHead,
    );
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (ledger_id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `/delete-ledger/${ledger_id}`,
      });
      showSuccess("Ledger deleted");
      fetchLedger(
        pagination.current,
        pagination.pageSize,
        mobileSearch,
        selectedAccountHead,
      );
    } catch {
      showError("Failed to delete Ledger");
    }
  };

  const handleStatusToggle = async (ledgerId: number) => {
    setLedgers((prev) =>
      prev.map((item) =>
        item.ledger_id === ledgerId
          ? { ...item, ledger_status: item.ledger_status === 1 ? 0 : 1 }
          : item,
      ),
    );

    try {
      await apiRequest({
        method: "put",
        url: `/update-status-h-ledger/${ledgerId}`,
      });
      showSuccess("Ledger status updated");
    } catch (error) {
      showError("Failed to update status");
      // revert toggle if failed
      setLedgers((prev) =>
        prev.map((item) =>
          item.ledger_id === ledgerId
            ? { ...item, ledger_status: item.ledger_status === 1 ? 0 : 1 }
            : item,
        ),
      );
    }
  };

  const handleTableChange: TableProps<Ledger>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleImportSuccess = () => {
    fetchLedger();
  };
  const fetchAccountHeads = async () => {
    if (hasFetchedAccountHeads.current) return; // prevent re-fetch
    try {
      const data = await apiRequest<any[]>({
        method: "get",
        url: `account-heads?status=1`,
      });
      const formatted = data.map((item) => ({
        label: item.ah_name,
        value: item.ah_id,
      }));
      setAccountHeadOptions(formatted);
      hasFetchedAccountHeads.current = true; // set flag
    } catch (error) {
      showError("Failed to fetch Ledger options");
    }
  };
  useEffect(() => {
    fetchAccountHeads();
  }, []);
  // On date change
  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    setDateRange(dates);

    const startDate = dates?.[0]?.format("YYYY-MM-DD");
    const endDate = dates?.[1]?.format("YYYY-MM-DD");

    fetchLedger(
      1,
      pagination.pageSize,
      mobileSearch,
      selectedAccountHead,
      startDate,
      endDate,
    );
  };

  // Define your columns, add more columns if you want to display more ledger fields
  const columns: TableColumnsType<Ledger> = [
    {
      title: "Company Name",
      dataIndex: "ledger_company_name",
      key: "ledger_company_name",
      filters: Array.from(
        new Set(
          ledgers
            .map((s) => s.ledger_company_name)
            .filter((name): name is string => Boolean(name)),
        ),
      ).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: "tree",
      filteredValue: filteredInfo.ledger_company_name || null,
      onFilter: (value, record) =>
        record.ledger_company_name?.includes(value as string) ?? false,
      sorter: (a, b) =>
        (a.ledger_company_name ?? "").localeCompare(
          b.ledger_company_name ?? "",
        ),
      sortOrder:
        sortedInfo.columnKey === "ledger_company_name"
          ? sortedInfo.order
          : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
      render: (_, record) => <div>{record.ledger_company_name ?? "-"}</div>,
    },
    {
      title: "Account Head",
      dataIndex: "ah_name",
      key: "ah_name",
      ellipsis: true,
      render: (_, record) => <div>{record.ah_name || "-"}</div>,
    },
    {
      title: "Mobile No",
      dataIndex: "ledger_mobile_no",
      key: "ledger_mobile_no",
      ellipsis: true,
      render: (_, record) => <div>{record.ledger_mobile_no || "-"}</div>,
    },
    // add more columns here as needed ...
  ];

  // Conditionally add action column if user has edit/delete rights
  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      width: 200,
      render: (_: any, record: Ledger) => (
        <Space size="middle">
          {currentModule?.right_edit === "Y" && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.ledger_status}
                onChange={() => handleStatusToggle(record.ledger_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === "Y" && (
            <Popconfirm
              title="Delete this Ledger?"
              onConfirm={() => handleDelete(record.ledger_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  //   const handleImport = async () => {
  //     try {
  //         await apiRequest({
  //             method: 'post',
  //             url: '/import-ledgers',
  //             data: parsedData,
  //         });
  //         showSuccess('legders imported successfully!');
  //     } catch (err) {
  //         console.error(err);
  //         showError('Import failed. Check the data format.');
  //     }
  // };

  return (
    <>
      <Space
        style={{
          display: "flex",
          marginBottom: 16,
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Breadcrumb style={{ display: "inline-block", margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Ledger</Breadcrumb.Item>
        </Breadcrumb>

        <Flex gap="small" align="center">
          <div style={{ marginBottom: 16 }}>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              style={{ marginTop: 16, marginRight: 16 }}
            />
          </div>
          <Input.Search
            placeholder="Search Mobile No"
            allowClear
            onSearch={handleMobileSearch}
            style={{ width: 200, marginRight: 16, padding: 10 }}
            value={mobileSearch}
            onChange={(e) => setMobileSearch(e.target.value)}
            enterButton={<SearchOutlined />} // <-- search icon button
            size="middle"
            className="no-border-radius-search left-rounded"
          />
          <Select
            showSearch
            placeholder="Select Account Head"
            allowClear
            optionFilterProp="label"
            options={accountHeadOptions}
            value={selectedAccountHead}
            onChange={(value) => {
              setSelectedAccountHead(value);
              fetchLedger(1, pagination.pageSize, mobileSearch, value);
            }}
            style={{ width: 200, marginRight: 16 }}
          />

          {currentModule?.right_add === "Y" && (
            <Button type="primary" onClick={showCreateModal}>
              Add Ledger
            </Button>
          )}
          {currentModule?.right_import === "Y" && (
            <Flex gap="small">
              <Button
                type="primary"
                onClick={() => setIsImportModalVisible(true)}
              >
                Import Ledger
              </Button>
            </Flex>
          )}
        </Flex>
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Ledger>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="ledger_id"
          // dat
          dataSource={ledgers}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15, x: "max-content" }}
          locale={{ emptyText: "No ledger found" }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1} –{" "}
                {(pagination.current - 1) * pagination.pageSize +
                  ledgers.length}{" "}
                of {pagination.total}
              </span>
              <Pagination
                {...pagination}
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                  fetchLedger(
                    page,
                    pageSize,
                    mobileSearch,
                    selectedAccountHead,
                  );
                  // total={ledgers.length}
                  // showSizeChanger
                  // onChange={(page, pageSize) => {
                  //   setPagination((prev) => ({ ...prev, current: page, pageSize }));
                  //   fetchLedger(page, pageSize, mobileSearch, selectedAccountHead);
                }}
              />
            </Flex>
          )}
        />
      </div>

      <SviconLedgerModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
      <ImportLedgerModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};

export default SviconLegders;
